import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  error?: string;
  label?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, leftIcon, rightIcon, error, label, hint, id, ...rest },
  ref
) {
  const inputId = id || `i-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-slate-700 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none flex items-center"
          >
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          ref={ref}
          aria-invalid={!!error}
          className={cn(
            'block w-full rounded-md border bg-white text-sm text-slate-900',
            'h-10 transition-colors',
            'placeholder:text-slate-400',
            'disabled:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70',
            leftIcon ? 'pl-10' : 'px-3.5',
            rightIcon ? 'pr-10' : 'pr-3.5',
            !leftIcon && 'pl-3.5',
            error
              ? 'border-red-300 focus:border-red-500'
              : 'border-slate-200 focus:border-brand-500',
            className
          )}
          {...rest}
        />
        {rightIcon && (
          <span
            aria-hidden="true"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          >
            {rightIcon}
          </span>
        )}
      </div>
      {(error || hint) && (
        <p
          className={cn(
            'text-xs mt-1.5',
            error ? 'text-red-600' : 'text-slate-500'
          )}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});
